/**
 * Settings › Users & roles — `organization-build-book.md` §6.4,
 * `org-04-users-and-roles.png`.
 *
 * Who works here, and what each of them can reach. The list on the left and
 * the person on the right, because the question people come here with is
 * about one person ("why can Maria not approve this?") and the answer is
 * their grants — not a table of roles.
 *
 * It replaces a page that edited a role and a scope as two separate things.
 * They were never separate: a grant is a role **at a place** (0075), and the
 * screen that pretended otherwise let somebody give a person Owner with no
 * idea where it applied.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  PERMISSION_AREAS, PERMISSION_AREA_INFO, can, emptyMatrix,
  type MeResponse, type PermissionArea, type PermissionMatrix,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { formatDocumentDate, formatRelative } from '../lib/format.js'
import { KindTile } from '../builder/kind-tile.js'
import { RecordTabs } from '../record/record-tabs.js'
import { Avatar, Button, EmptyState, Segmented, Tag } from '../components/primitives.js'
import { ConfirmDialog, Popover } from '../components/overlays.js'
import { SelectInput } from '../components/form/index.js'
import { HistoryTab } from '../components/history-tab.js'
import { GrantAccessDialog } from '../components/org/dialogs.js'
import { LevelPill } from '../components/people/level-pill.js'
import { PersonHours } from '../components/people/person-hours.js'
import { InviteForm } from '../components/forms/invite-form.js'
import { IconMore, IconPlus, IconSearch } from '../frame/icons.js'
import { useSettingsBar } from '../settings/settings-bar.js'
import { PersonSellingCards } from '../components/people/person-selling.js'
import { PersonActivityCard } from '../settings/person-activity.js'

interface PersonRow {
  id: string
  name: string
  email: string
  phone: string | null
  status: 'invited' | 'active' | 'suspended'
  roleNames: string | null
  scopeNames: string | null
  grantsSummary: string | null
  app: string | null
  mfaEnrolled: boolean
  lastSeenAt: string | null
  hasPin: boolean
}

interface Grant {
  id: string
  orgUnitId: string
  orgUnitName: string
  kind: 'company' | 'region' | 'district' | 'branch'
  userId: string
  userName: string
  roleId: string
  roleName: string
  note: string
  grantedByName: string | null
  createdAt: string
}

interface Person extends PersonRow {
  firstName: string
  lastName: string
  title: string | null
  defaultApp: string | null
  homeOrgUnitId: string | null
  homeOrgUnitName: string | null
  sessions: number
  grants: Grant[]
}

/** The four the mockup's segmented row offers. */
const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'office', label: 'Office' },
  { value: 'crews', label: 'Crews' },
  { value: 'none', label: 'No access' },
] as const

type Filter = (typeof FILTERS)[number]['value']

export function People({ me }: { me: MeResponse }) {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState<PersonRow[]>([])
  const [person, setPerson] = useState<Person | null>(null)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [inviting, setInviting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canEdit = can(me.permissions, 'employees', 'edit')
  const selectedId = params.get('person')
  const tab = params.get('tab') ?? 'access'

  const select = useCallback((id: string, nextTab = 'access') => {
    setParams((was) => {
      const next = new URLSearchParams(was)
      next.set('person', id)
      next.set('tab', nextTab)
      return next
    }, { replace: true })
  }, [setParams])

  const load = useCallback(async () => {
    try {
      const { items } = await request<{ items: PersonRow[] }>(
        `/people${query ? `?q=${encodeURIComponent(query)}` : ''}`)
      setRows(items)
      setError(null)
      return items
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'The list did not load.')
      return []
    }
  }, [query])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (selectedId || rows.length === 0) return
    select(rows[0]!.id)
  }, [selectedId, rows, select])

  const reload = useCallback(async () => {
    if (!selectedId) { setPerson(null); return }
    try {
      setPerson(await request<Person>(`/people/${selectedId}`))
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That person did not load.')
    }
  }, [selectedId])

  useEffect(() => { void reload() }, [reload])

  const shown = useMemo(() => rows.filter((row) => {
    if (filter === 'office') return row.app !== 'crew_app'
    if (filter === 'crews') return row.app === 'crew_app'
    if (filter === 'none') return !row.grantsSummary
    return true
  }), [rows, filter])

  /*
   * Counted from `/roles`, not from the people on screen. A search that
   * narrows the list to one person would otherwise make the bar say "1 role",
   * which is a fact about the search rather than about the company.
   */
  const [roleCount, setRoleCount] = useState(0)
  useEffect(() => {
    void request<{ items: unknown[] }>('/roles')
      .then(({ items }) => setRoleCount(items.length))
      .catch(() => undefined)
  }, [])

  const barActions = useSettingsBar({
    title: 'Users & roles',
    sub: `${rows.length} ${rows.length === 1 ? 'person' : 'people'} · ${roleCount} `
      + `${roleCount === 1 ? 'role' : 'roles'} · access is a role at a place`,
    actions: (
      <>
        <Button className="h-8 text-[12.5px]" onClick={() => navigate('/settings/roles')}>
          Roles &amp; permissions
        </Button>
        {canEdit && (
          <Button variant="primary" className="h-8 text-[12.5px]" onClick={() => setInviting(true)}>
            <IconPlus size={15} />Invite
          </Button>
        )}
      </>
    ),
  })

  return (
    <div className="flex items-start gap-5 p-5 max-lg:flex-col">
      {barActions}

      <div className={cn('flex w-[320px] shrink-0 flex-col self-start overflow-hidden max-lg:w-full',
        'rounded-record border border-line bg-surface')}>
        <div className="flex gap-2 p-3 pb-2">
          <label className="relative block min-w-0 flex-1">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint">
              <IconSearch size={14} />
            </span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${rows.length} people`}
              aria-label="Search people"
              className={cn('h-[34px] w-full rounded-field border border-line bg-surface pl-8 pr-2.5',
                'text-cell text-text placeholder:text-text-faint',
                'focus:border-accent-line focus:outline-none focus:ring-2 focus:ring-accent-ring')}
            />
          </label>
        </div>

        <div className="px-3 pb-2">
          <Segmented
            label="Which people"
            value={filter}
            onChange={setFilter}
            options={FILTERS.map((one) => ({ value: one.value, label: one.label }))}
            full
          />
        </div>

        <div className="flex flex-col pb-2">
          {shown.length === 0 && (
            <p className="m-0 px-4 py-3 text-body text-text-faint">
              {query ? `Nobody matches “${query}”.` : 'Nobody here yet.'}
            </p>
          )}
          {shown.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => select(row.id, tab)}
              className={cn('mx-2 flex items-center gap-2.5 rounded-[10px] px-3 py-[9px] text-left',
                row.id === selectedId
                  ? 'border border-accent-line bg-accent-soft'
                  : 'border border-transparent hover:bg-hover')}
            >
              <Avatar name={row.name} size={30} />
              <span className="flex min-w-0 flex-col">
                <span className="flex items-center gap-1.5">
                  <b className="truncate text-[13px] text-text">{row.name}</b>
                  {row.status === 'invited' && <Tag tone="warn">Invited</Tag>}
                  {row.status === 'suspended' && <Tag>No sign-in</Tag>}
                </span>
                <span className="truncate text-[11.5px] text-text-muted">
                  {row.grantsSummary ?? 'No access yet'}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {person
        ? (
          <PersonCard
            me={me}
            person={person}
            canEdit={canEdit}
            tab={tab}
            onTab={(next) => select(person.id, next)}
            onChanged={() => { void reload(); void load() }}
          />
        )
        : (
          <div className="min-w-0 flex-1 rounded-record border border-line bg-surface">
            <EmptyState
              title={error ?? 'Choose somebody'}
              body={error
                ? 'Try again, or ask somebody who can see them.'
                : 'Pick a person on the left to see where they work and what they can do.'}
            />
          </div>
        )}

      {inviting && (
        <InviteForm
          onClose={() => setInviting(false)}
          onInvited={(userId) => {
            setInviting(false)
            void load().then(() => select(userId))
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function PersonCard({ me, person, canEdit, tab, onTab, onChanged }: {
  me: MeResponse
  person: Person
  canEdit: boolean
  tab: string
  onTab: (tab: string) => void
  onChanged: () => void
}) {
  const [granting, setGranting] = useState(false)
  const [changing, setChanging] = useState<Grant | null>(null)
  const [removing, setRemoving] = useState<Grant | null>(null)
  const [deactivating, setDeactivating] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const first = person.firstName || person.name.split(' ')[0] || person.name

  async function act(path: string, done: string) {
    try {
      await request(`/people/${person.id}/${path}`, { method: 'POST', body: {} })
      setNotice(done)
      onChanged()
    } catch (problem) {
      setNotice((problem as { message?: string }).message ?? 'That did not work.')
    }
  }

  async function removeGrant(grant: Grant) {
    try {
      await request(`/people/${person.id}/grants/${grant.id}`, { method: 'DELETE' })
      setRemoving(null)
      onChanged()
    } catch (problem) {
      setNotice((problem as { message?: string }).message ?? 'That grant stays.')
      setRemoving(null)
    }
  }

  const meta = [
    person.email,
    person.phone,
    APP_WORDS[person.defaultApp ?? 'office'] ?? null,
    person.lastSeenAt ? `signed in ${formatRelative(person.lastSeenAt)}` : 'never signed in',
  ].filter(Boolean).join(' · ')

  return (
    <div className={cn('flex min-w-0 flex-1 flex-col self-start overflow-hidden',
      'max-w-[900px] rounded-record border border-line bg-surface',
      'max-lg:w-full max-lg:max-w-full')}>

      <div className="flex items-center gap-3.5 px-[18px] pb-3 pt-4 max-sm:flex-wrap">
        <Avatar name={person.name} size={48} />
        <div className="flex min-w-0 flex-col gap-1">
          <b className="truncate text-[18px] -tracking-[0.01em] text-text">{person.name}</b>
          <span className="truncate text-[12.5px] text-text-muted">{meta}</span>
          <span className="flex flex-wrap gap-1.5 pt-0.5">
            {person.mfaEnrolled && <Tag tone="ok">Two-step on</Tag>}
            {person.hasPin && <Tag>Crew PIN set</Tag>}
            {person.status === 'suspended' && <Tag tone="bad">No sign-in</Tag>}
            {person.status === 'invited' && <Tag tone="warn">Invite waiting</Tag>}
          </span>
        </div>

        {canEdit && (
          <span className="ml-auto flex shrink-0 gap-2 max-sm:ml-0 max-sm:w-full">
            <Button
              className="h-8 text-[12.5px]"
              onClick={() => void act('reinvite', `A new link is on its way to ${person.email}.`)}
            >
              Send reset
            </Button>
            {person.status === 'suspended'
              ? (
                <Button
                  className="h-8 text-[12.5px]"
                  onClick={() => void act('reactivate', `${first} can sign in again.`)}
                >
                  Let them back in
                </Button>
              )
              : (
                <Button
                  variant="danger"
                  className="h-8 text-[12.5px]"
                  onClick={() => setDeactivating(true)}
                >
                  Deactivate
                </Button>
              )}
          </span>
        )}
      </div>

      {notice && (
        <p className="m-0 px-[18px] pb-2 text-[12.5px] text-text-muted">{notice}</p>
      )}

      <RecordTabs
        active={tab}
        onChange={onTab}
        tabs={[
          { key: 'access', label: 'Access', icon: 'access', count: person.grants.length },
          { key: 'profile', label: 'Profile', icon: 'user' },
          { key: 'activity', label: 'Activity', icon: 'history' },
          /*
           * The office's half of My hours. It is read-only on purpose: a
           * punch is corrected on the iPad by the lead, as a new immutable
           * event (§4.3), and a second door into that ledger from a settings
           * panel is how two people end up correcting the same day.
           */
          { key: 'hours', label: 'Hours', icon: 'hours' },
        ]}
      />

      {tab === 'access' && (
        <>
          <div className="flex items-center gap-2 px-[18px] pb-1.5 pt-3.5">
            <b className="text-[13.5px] text-text">Access</b>
            <span className="min-w-0 truncate text-[12px] text-text-muted">
              a role, and where it applies · the strongest wins where they overlap
            </span>
            {canEdit && (
              <Button
                variant="primary"
                className="ml-auto h-8 shrink-0 text-[12.5px]"
                onClick={() => setGranting(true)}
              >
                <IconPlus size={15} />Add access
              </Button>
            )}
          </div>

          {person.grants.length === 0 && (
            <p className="m-0 px-[18px] pb-3 text-[12.5px] text-text-faint">
              {first} has no access yet, so nothing in Rowted is visible to them.
            </p>
          )}

          {person.grants.map((grant) => (
            <div
              key={grant.id}
              className="flex items-center gap-3 border-b border-line-faint px-[18px] py-3 last:border-b-0"
            >
              <KindTile kind={grant.kind} size={32} />
              <span className="flex min-w-0 flex-1 flex-col gap-px">
                <span className="flex items-center gap-2">
                  <b className="truncate text-[13.5px] text-text">{grant.orgUnitName}</b>
                  <Tag tone="ok">{grant.roleName}</Tag>
                </span>
                <span className="truncate text-[12px] text-text-muted">
                  since {formatDocumentDate(grant.createdAt)}
                  {grant.grantedByName ? ` · granted by ${grant.grantedByName}` : ''}
                  {grant.note ? ` · ${grant.note}` : ''}
                </span>
              </span>
              {canEdit && (
                <GrantMenu
                  grant={grant}
                  onChangeRole={() => setChanging(grant)}
                  onRemove={() => setRemoving(grant)}
                />
              )}
            </div>
          ))}

          <CanDo person={person} first={first} />
        </>
      )}

      {tab === 'profile' && <Profile person={person} canEdit={canEdit} onSaved={onChanged} />}

      {tab === 'hours' && <PersonHours personId={person.id} first={first} />}

      {tab === 'activity' && (
        <div className="px-[18px] py-3">
          <HistoryTab table="users" recordId={person.id} />
        </div>
      )}

      {(granting || changing) && (
        <GrantAccessDialog
          me={me}
          person={{ id: person.id, name: person.name }}
          atUnitId={changing?.orgUnitId}
          onClose={() => { setGranting(false); setChanging(null) }}
          onGranted={() => { setGranting(false); setChanging(null); onChanged() }}
        />
      )}

      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.roleName} at ${removing.orgUnitName}?`}
          confirmLabel="Remove access"
          onCancel={() => setRemoving(null)}
          onConfirm={() => void removeGrant(removing)}
          body={`${first} keeps their other access, their hours and everything they have `
            + 'worked on. The change is written to the audit log.'}
        />
      )}

      {deactivating && (
        <ConfirmDialog
          title={`Switch off ${person.name}'s sign-in?`}
          confirmLabel="Deactivate"
          busyLabel="Switching off…"
          onCancel={() => setDeactivating(false)}
          onConfirm={() => {
            setDeactivating(false)
            void act('deactivate', `${first} can no longer sign in.`)
          }}
          body={`${first} keeps every grant, every hour and every job they have worked on, and `
            + 'gets all of it back the day they come back. What stops is signing in — including '
            + `${person.sessions === 1 ? 'the device' : `the ${person.sessions} devices`} `
            + 'signed in as them right now.'}
        />
      )}
    </div>
  )
}

const APP_WORDS: Record<string, string> = {
  office: 'Office app', crew_app: 'Crew app',
}

function GrantMenu({ grant, onChangeRole, onRemove }: {
  grant: Grant; onChangeRole: () => void; onRemove: () => void
}) {
  const [open, setOpen] = useState(false)
  const [at, setAt] = useState<HTMLElement | null>(null)

  return (
    <>
      <span ref={setAt} className="inline-flex shrink-0">
        <Button
          variant="ghost"
          aria-label={`More for ${grant.orgUnitName}`}
          className="h-[30px] w-[30px] p-0"
          onClick={() => setOpen((was) => !was)}
        >
          <IconMore size={15} />
        </Button>
      </span>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={at}
        align="right"
        width={186}
        label={`More for ${grant.orgUnitName}`}
      >
        <div className="flex flex-col py-1">
          <button
            type="button"
            onClick={() => { setOpen(false); onChangeRole() }}
            className="flex h-9 items-center px-3 text-left text-cell text-text hover:bg-hover"
          >
            Change role
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onRemove() }}
            className="flex h-9 items-center px-3 text-left text-cell text-danger hover:bg-danger-bg"
          >
            Remove
          </button>
        </div>
      </Popover>
    </>
  )
}

/**
 * "What Maria can do" — the readout that makes grants mean something.
 *
 * A list of grants says where somebody has been given a role. It does not say
 * what they can do *in York*, which is the question, and which no single
 * grant answers: the answer is the strongest of every grant at York or above
 * it, and it is computed by the server (`GET /people/:id/permissions-at`)
 * with the same function the API enforces with.
 */
function CanDo({ person, first }: { person: Person; first: string }) {
  const places = person.grants
  const [at, setAt] = useState<string | null>(places[0]?.orgUnitId ?? null)
  const [matrix, setMatrix] = useState<PermissionMatrix>(emptyMatrix())
  const [from, setFrom] = useState<Partial<Record<PermissionArea, string>>>({})

  useEffect(() => {
    setAt(places[0]?.orgUnitId ?? null)
  }, [person.id, places])

  useEffect(() => {
    if (!at) return
    let live = true
    void request<{ permissions: PermissionMatrix; from: Partial<Record<PermissionArea, string>> }>(
      `/people/${person.id}/permissions-at?orgUnitId=${at}`)
      .then((answer) => { if (live) { setMatrix(answer.permissions); setFrom(answer.from) } })
      .catch(() => undefined)
    return () => { live = false }
  }, [person.id, at])

  if (places.length === 0) return null

  const chosen = places.find((one) => one.orgUnitId === at)
  const other = places.find((one) => one.orgUnitId !== at)

  return (
    <div className="border-t border-line-faint">
      <div className="flex flex-wrap items-center gap-2 px-[18px] pb-1.5 pt-3.5">
        <b className="text-[13.5px] text-text">What {first} can do</b>
        <span className="text-[12px] text-text-muted">in</span>
        <span className="w-[180px]">
          <SelectInput
            label={`Where to show ${first}'s permissions`}
            value={at}
            onChange={setAt}
            options={places.map((one) => ({ value: one.orgUnitId, label: one.orgUnitName }))}
          />
        </span>
        {chosen && (
          <span className="text-[12px] text-text-faint">
            from {chosen.roleName} at {chosen.orgUnitName}
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-x-4 gap-y-1.5 px-[18px] pb-4 max-lg:grid-cols-2 max-sm:grid-cols-1">
        {PERMISSION_AREAS.map((area) => (
          <span
            key={area}
            title={from[area] ? `from ${from[area]}` : undefined}
            className="flex items-center justify-between border-b border-line-faint py-1 text-[12.5px]"
          >
            <span className="min-w-0 truncate text-text">{PERMISSION_AREA_INFO[area].label}</span>
            <LevelPill level={matrix[area]} />
          </span>
        ))}
      </div>

      {other && (
        <p className="m-0 px-[18px] pb-3.5 text-[12px] leading-[1.45] text-text-muted">
          Pick <b className="font-semibold">{other.orgUnitName}</b> above to see the{' '}
          {other.roleName} set. Somewhere {first} has no grant at all shows nothing, and they
          never see it.
        </p>
      )}
    </div>
  )
}

function Profile({ person, canEdit, onSaved }: {
  person: Person; canEdit: boolean; onSaved: () => void
}) {
  const [app, setApp] = useState(person.defaultApp ?? 'office')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(next: string) {
    setApp(next)
    setBusy(true)
    setError(null)
    try {
      await request(`/people/${person.id}`, { method: 'PATCH', body: { defaultApp: next } })
      onSaved()
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  const facts: [string, string][] = [
    ['Email', person.email],
    ['Mobile', person.phone ?? '—'],
    ['Title', person.title ?? '—'],
    ['Based at', person.homeOrgUnitName ?? '—'],
    ['Two-step', person.mfaEnrolled ? 'On' : 'Not set up'],
    ['Crew PIN', person.hasPin ? 'Set' : 'Not set'],
    ['Signed in on', person.sessions === 1 ? '1 device' : `${person.sessions} devices`],
  ]

  return (
    <div className="flex flex-col">
      {/* Board 12 A: what they sell and their working day, on the page grey under the tabs, before the facts. */}
      <PersonSellingCards userId={person.id} className="border-b border-line bg-bg px-[22px] py-[18px]" />
      {/* Activity's card (`docs/activity.md` §12.2, Board 17 B): grants, and who can see this person. */}
      <PersonActivityCard personId={person.id} canEdit={canEdit}
        className="border-b border-line bg-bg px-[22px] py-[18px]" />

      <div className="flex flex-col gap-3 px-[18px] py-3.5">
        <div className="grid grid-cols-2 gap-x-3.5 gap-y-2.5 max-sm:grid-cols-1">
          {facts.map(([label, value]) => (
            <span key={label} className="flex flex-col gap-px">
              <span className="text-[11.5px] text-text-muted">{label}</span>
              <span className="truncate text-[13px] font-semibold text-text">{value}</span>
            </span>
          ))}

          <span className="flex flex-col gap-1">
            <span className="text-[11.5px] text-text-muted">Opens in</span>
            <SelectInput
              label="Opens in"
              value={app}
              disabled={!canEdit || busy}
              onChange={(value) => value && void save(value)}
              options={[
                { value: 'office', label: 'Office app' },
                { value: 'crew_app', label: 'Crew app' },
              ]}
            />
            <span className="text-[11px] text-text-faint">
              from the role · change it per person here
            </span>
          </span>
        </div>

        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}

        <p className="m-0 text-[12px] leading-[1.45] text-text-muted">
          Name, photo and contact details are the person&apos;s own to change under{' '}
          <b className="font-semibold">My account</b>.
        </p>
      </div>
    </div>
  )
}
